'use strict';

import VZSpinner from 'Components/VZSpinner';
import * as debounce from 'lodash/debounce';
import { appendParamToURL, getParamFromURL, getUrlFromAttributes, removeParamFromURL } from 'Util/urlUtils';
import Alert from 'Components/Alert';
import Animation from '../../Animation';
import { scrollToElement } from 'Util/domUtils';
import VZConfirmationModal from '../../ConfirmationModal';

const PRODUCT_CARD_SELECTORS = 'vz-product-card, vz-tc-product-card';

export default class VZQuantitySelector extends HTMLElement {
    constructor() {
        super();
        this.excludedAttributes = ['class', 'data-configurator-type', 'data-url', 'data-general-error'];

        this.onInput = this.onInput.bind(this);
        this.toggleIncreaseAndDecreaseButtons = this.toggleIncreaseAndDecreaseButtons.bind(this);
        this.confirmRemove = this.getAttribute('data-confirm-remove') === 'true';
    }

    connectedCallback() {
        this.spinner = document.querySelector('vz-spinner') || new VZSpinner();
        this.sidebar = document.querySelector('.js-configurator-sidebar');
        this.editMode = document.querySelector('.js-edit-mode');

        // reset ID to ensure unique selector and native label feature still works
        this.setAttribute('id', Date.now());

        this.forbiddenValues = ['e', '+', ',', '-', '.', 69, 187, 188, 189, 190];
        this.debounceRate = parseFloat(this.getAttribute('debounce-rate')) || 500;
        this.debouncedChange = debounce(this.onChange.bind(this), this.debounceRate);
        this.quantityField = this.querySelector('.js-quantity-field');
        this.formControl = this.querySelector('.form-control');
        this.quantityFieldOrgValue = this.quantityField.value;
        this.productQuantityField = document.querySelector('.js-quantity-selector');

        if (this.quantityField.value !== '0') {
            this.formControl.classList.add('border-turquoise');
        }

        this.quantityField.addEventListener('blur', this.onBlur.bind(this));
        this.addEventListener('input', this.onInput.bind(this));
        this.addEventListener('keydown', this.onKeyDown.bind(this));
        this.addEventListener('paste', this.onPaste);
        this.businessMobileWrapper = this.closest('.js-business-mobile-quantity-card');

        [...this.querySelectorAll('.js-quantity-decrease, .js-quantity-increase')].forEach((element) => {
            element.addEventListener('click', this.decreaseOrIncrease.bind(this));
        });
    }

    /**
     * @function toggleIncreaseAndDecreaseButtons
     * @description Toggles the increase and decrease button state based on the string passed in. This is so that
     * values are not changeable while a network call is happening.
     * @param {string} disableOrEnable - Expects 'disable' or 'enable'.
     */
    toggleIncreaseAndDecreaseButtons(disableOrEnable) {
        const buttons = [...this.querySelectorAll('.js-quantity-decrease, .js-quantity-increase')];
        buttons.forEach((button) => (disableOrEnable === 'disable' ? button.setAttribute('disabled', true) : button.removeAttribute('disabled')));
    }

    onBlur(evt) {
        if (evt.target.value.length !== 0) return;
        evt.target.value = '0';
        this.validateMinMax();
        this.debouncedChange();
    }

    onInput(evt) {
        if (evt.target.value.length === 0) return;

        this.validateMinMax();
        this.debouncedChange();
    }

    onKeyDown(evt) {
        if (this.forbiddenValues.indexOf(evt.key || evt.keyCode) !== -1) {
            evt.preventDefault();
        }
    }

    onPaste(evt) {
        // Replace all non-digits on copy paste
        evt.preventDefault();
        const newValue = evt.clipboardData.getData('text').replace(/\D/g, '');
        evt.target.value = ''; // required, otherwise it won't remove the dot
        evt.target.value = newValue;

        this.validateMinMax();
        this.debouncedChange();
    }

    decreaseOrIncrease(evt) {
        evt.preventDefault();

        const field = this.quantityField;
        const currentValue = parseInt(field.value, 10);
        const decrease = evt.target.classList.contains('js-quantity-decrease');
        const manuallyTriggered = evt.isTrusted;

        // Show a confirmation modal when the quantity is manually decreased to 0, and the confirmation modal is enabled.
        if (this.confirmRemove && decrease && currentValue === 1 && manuallyTriggered) {
            const confirmationModal = new VZConfirmationModal();
            confirmationModal.toggleConfirmationModal().then((confirmed) => {
                if (confirmed) {
                    this.updateQuantity(field, 0);
                }
            });
        } else {
            this.updateQuantity(field, decrease ? currentValue - 1 : currentValue + 1);
        }
    }

    updateQuantity(field, newValue) {
        if (window.isNaN(newValue)) {
            field.value = field.min;
        } else {
            field.value = newValue;
        }

        this.validateMinMax();
        this.debouncedChange();

        // Highlight info card if applicable
        if (this.quantityField.classList.contains('js-highlight-container-when-not-empty')) this.highlightInfoCard(field.value !== '0');
    }

    highlightInfoCard(highlight) {
        const infoCard = this.quantityField.closest('.js-info-card');

        if (infoCard) infoCard.classList.toggle('info-card--bordered-primary', highlight);
    }

    validateMinMax() {
        const field = this.quantityField;
        const moreThanMax = parseInt(field.value, 10) > field.max;
        const lessThanMin = parseInt(field.value, 10) < field.min;

        if (field.max && moreThanMax) field.value = field.max;
        if (lessThanMin) {
            field.value = field.min;

            // Display an alert when an attempt is made to reduce the quantity below the existing number of phones on Business Mobile"
            if (field.value > 1 && this.businessMobileWrapper) {
                if (this.businessMobileWrapper.innerHTML.includes('alert')) {
                    Animation.shakeSubtle(this.businessMobileWrapper.querySelector('.alert'));
                } else {
                    const alertTitle = this.businessMobileWrapper.getAttribute('alert-title');
                    const alertDescription = this.businessMobileWrapper.getAttribute('alert-desc');
                    const alert = new Alert(this.businessMobileWrapper.querySelector('.js-quantity-selector-wrapper'));
                    alert.show(alertTitle, alertDescription, 'danger', 'mt-2');

                    // Scroll to BM Devices
                    const addDevicesLink = document.querySelector('.js-add-devices-link');
                    const devicesContainer = document.querySelector('.js-hardware-device-cards');
                    if (addDevicesLink && devicesContainer) {
                        addDevicesLink.addEventListener('click', (event) => {
                            event.preventDefault();
                            scrollToElement(devicesContainer);
                        });
                    }
                }
            }
        }
    }

    onChange() {
        const field = this.quantityField;

        // Double check to see if there has been an actual change in value
        if (field.value === this.quantityFieldOrgValue) return;

        field.setAttribute('value', field.value);
        field.dispatchEvent(
            new CustomEvent('fieldUpdated', {
                detail: {
                    id: Date.now(),
                    value: field.value
                }
            })
        );

        if (this.quantityField.value !== '0') {
            this.formControl.classList.add('border-turquoise');
        }

        const vZProductCard = this.closest(PRODUCT_CARD_SELECTORS);
        const urlAttributeValue = this.quantityField.getAttribute('data-url');
        if (!urlAttributeValue || !vZProductCard) {
            return;
        }

        let fieldUrl = getUrlFromAttributes(urlAttributeValue, vZProductCard, this.excludedAttributes);
        fieldUrl = removeParamFromURL(fieldUrl, 'quantity');
        fieldUrl = appendParamToURL(fieldUrl, 'quantity', this.productQuantityField.value);

        // Disable the buttons - Has to be before the return statement to work for one net quantity selectors.
        this.toggleIncreaseAndDecreaseButtons('disable');

        if (!fieldUrl) return;

        if (this.editMode) {
            const pliId = getParamFromURL(window.location.href, 'pliId');
            fieldUrl = appendParamToURL(fieldUrl, 'pliId', pliId);
        }

        this.spinner.start();

        fetch(fieldUrl)
            .then((res) => res.text())
            .then((response) => {
                vZProductCard.dispatchEvent(new CustomEvent('variationChange', { detail: { response: response } }));
            })
            .finally(() => {
                this.toggleIncreaseAndDecreaseButtons('enable'); // Enable buttons
                this.spinner.stop();
            });
    }
}

if (!window.customElements.get('vz-quantity-selector')) {
    window.customElements.define('vz-quantity-selector', VZQuantitySelector);
}
